import { isValidCPF } from '@brazilian-utils/brazilian-utils'
import Papa from 'papaparse'

/**
 * Planilha da importação de CPFs na etiqueta (POST /chains/{chainId}/tag/{id}/import-cpfs).
 * Só extrai as células: a validação do CPF é do backend, que devolve as linhas inválidas na prévia.
 * `line` é a linha na planilha (1 = primeira linha do arquivo), pra o operador achar o erro.
 */
export interface CpfSheetRow { line: number; cpf: string; name: string | null }

/** Excel em português salva CSV em windows-1252: lido como UTF-8, "João" vira "Jo�o". */
export const decodeCsv = (buffer: ArrayBuffer): string => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
  } catch {
    return new TextDecoder('windows-1252').decode(buffer)
  }
}

/**
 * Modelo do botão "Baixar modelo". BOM para o Excel abrir como UTF-8 e `;` para abrir em colunas.
 * O CPF de exemplo é inválido de propósito: se o operador esquecer a linha, ela só aparece como ignorada.
 */
export const CPF_SHEET_TEMPLATE = '\uFEFFCPF;Nome\r\n000.000.000-00;Maria Exemplo (apague esta linha)\r\n'

/** CSV do Excel em português vem com `;`; o papaparse detecta o separador. */
export const csvToMatrix = (text: string): unknown[][] => Papa.parse<unknown[]>(text, { skipEmptyLines: true }).data

const headerKey = (cell: unknown) => String(cell ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()

// Excel guarda CPF como número e come o zero da frente: 01234567890 vira 1234567890.
const cpfDigits = (cell: unknown) => {
  const digits = String(cell ?? '').replace(/\D/g, '')
  return digits && digits.length < 11 ? digits.padStart(11, '0') : digits
}

export const parseCpfSheet = (matrix: unknown[][]): CpfSheetRow[] => {
  const header = (matrix[0] || []).map(headerKey)
  const hasHeader = header.includes('cpf')
  const cpfCol = hasHeader ? header.indexOf('cpf') : 0
  const nameCol = hasHeader ? header.indexOf('nome') : -1
  const skipFirst = hasHeader || !isValidCPF(cpfDigits(matrix[0]?.[0]))

  return matrix.flatMap((row, index) => {
    if (index === 0 && skipFirst) { return [] }
    const cpf = cpfDigits(row[cpfCol])
    const name = nameCol >= 0 ? String(row[nameCol] ?? '').trim().slice(0, 200) || null : null
    return cpf || name ? [{ line: index + 1, cpf, name }] : []
  })
}
